/**
 * Ayarlar › Boşta kalınca pano göster (#570): bekleme süresi, panolar, dönüş aralığı.
 * Pano listesi kullanıcıya atanmış panolar (DashBoardUsers); liste boşsa seçenek yerine not.
 */
import { useTranslation } from 'react-i18next';
import type { ComponentType, ReactNode } from 'react';
import { usePrefsStore } from '@/entities/preferences/model/prefsStore';
import type { Preferences } from '@/entities/preferences/model/schema';
import { useDashboards } from '@/features/shell/navMenu/DashboardsGroup';
import { Checkbox } from '@/shared/ui/Checkbox';
import { Input } from '@/shared/ui/Input';
import { Switch } from '@/shared/ui/Switch';

type IdlePrefs = Preferences['idleDashboard'];
type RowComponent = ComponentType<{ label: string; hint?: string; children: ReactNode }>;

export function IdleDashboardSettings({ Row }: { Row: RowComponent }) {
  const { t } = useTranslation();
  const prefs = usePrefsStore((s) => s.prefs.idleDashboard);
  const set = usePrefsStore((s) => s.set);
  const dashboards = useDashboards();
  const patch = (p: Partial<IdlePrefs>) => set({ idleDashboard: { ...prefs, ...p } });
  const selected = new Set(prefs.dashboardIds.map((id) => id.toLowerCase()));
  const toggle = (id: string) => {
    const key = id.toLowerCase();
    const next = selected.has(key)
      ? prefs.dashboardIds.filter((x) => x.toLowerCase() !== key)
      : [...prefs.dashboardIds, id];
    patch({ dashboardIds: next });
  };
  const num = (v: string, lo: number, hi: number, fallback: number) => {
    const n = Math.floor(Number(v));
    return Number.isFinite(n) ? Math.min(hi, Math.max(lo, n)) : fallback;
  };

  return (
    <>
      <Row label={t('prefs.idleDashboard.label')} hint={t('prefs.idleDashboard.hint')}>
        <Switch checked={prefs.enabled} onCheckedChange={(v) => patch({ enabled: v })} />
      </Row>
      {prefs.enabled ? (
        <div className="flex flex-col gap-2 py-2 text-sm" data-idle-dashboard-settings>
          <label className="flex items-center justify-between gap-4">
            <span>{t('prefs.idleDashboard.minutes')}</span>
            <Input
              type="number"
              min={1}
              max={240}
              value={prefs.minutes}
              onChange={(e) => patch({ minutes: num(e.target.value, 1, 240, 5) })}
              className="w-24 text-right"
            />
          </label>
          <div>
            <div className="mb-1">{t('prefs.idleDashboard.dashboards')}</div>
            {dashboards.length ? (
              <div className="flex flex-col gap-1">
                {dashboards.map((d) => (
                  <label key={d.dashboardUserId} className="flex cursor-pointer items-center gap-2">
                    <Checkbox
                      checked={selected.has(d.dashboardId.toLowerCase())}
                      onCheckedChange={() => toggle(d.dashboardId)}
                    />
                    <span className="truncate">{d.dashboard!.dashboardName}</span>
                  </label>
                ))}
              </div>
            ) : (
              <p className="text-fg-muted text-xs">{t('prefs.idleDashboard.noDashboards')}</p>
            )}
          </div>
          {prefs.dashboardIds.length > 1 ? (
            <label className="flex items-center justify-between gap-4">
              <span>{t('prefs.idleDashboard.rotate')}</span>
              <Input
                type="number"
                min={10}
                max={3600}
                value={prefs.rotateSec}
                onChange={(e) => patch({ rotateSec: num(e.target.value, 10, 3600, 60) })}
                className="w-24 text-right"
              />
            </label>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
